<template>
  <div class="irp-opportunities-tab" data-testid="irp-opportunities-tab">
    <template v-if="!hasError">
      <div class="irp-opportunities-tab__header">
        <h3 class="irp-opportunities-tab__heading">Opportunity information</h3>
        <IRPExpandToggle
          v-if="ownList?.hasHiddenFields"
          v-model:is-expanded="isOwnExpanded"
          :cta="IRP_CTAS.OWN_DATA"
          data-testid="irp-opportunities-tab-expand-toggle"
        />
      </div>
      <div :class="{ 'irp-opportunities-tab__card': hasOwnOpportunities }">
        <div
          v-if="isInitialLoading"
          class="irp-opportunities-tab__skeleton"
          data-testid="irp-opportunities-tab-skeleton"
        >
          <BittsBone v-for="row in SKELETON_ROW_COUNT" :key="row" type="copy" />
        </div>
        <IRPOpportunityList
          v-else
          ref="ownList"
          :opportunities="ownOpportunities"
          :is-own-data="true"
          empty-message="No opportunities on this record"
        />
      </div>
    </template>

    <IRPPartnerDataSection section="opportunities">
      <template #default="{ partner }">
        <div class="irp-opportunities-tab__card">
          <IRPOpportunityList :opportunities="partner.opportunities.items" />
        </div>
      </template>
    </IRPPartnerDataSection>
  </div>
</template>

<script setup lang="ts">
import { BittsBone } from '@crossbeam/bitts';
import { IRP_CTAS } from '@crossbeam/itly';

import { computed, useTemplateRef } from 'vue';

import { useIndividualRecordStore } from '@/stores';

import IRPExpandToggle from './IRPExpandToggle.vue';
import IRPOpportunityList from './IRPOpportunityList.vue';
import IRPPartnerDataSection from './IRPPartnerDataSection.vue';

const SKELETON_ROW_COUNT = 3;

const individualRecordStore = useIndividualRecordStore();

const isInitialLoading = computed(
  () =>
    !!individualRecordStore.opportunities?.isLoading &&
    !individualRecordStore.opportunities?.hasLoaded,
);

const hasError = computed(() => !!individualRecordStore.opportunities?.error);

const ownOpportunities = computed(
  () => individualRecordStore.opportunities?.own?.opportunities.items ?? [],
);

const hasOwnOpportunities = computed(
  () => isInitialLoading.value || !!ownOpportunities.value.length,
);

const ownList =
  useTemplateRef<InstanceType<typeof IRPOpportunityList>>('ownList');

const isOwnExpanded = computed({
  get: () => !!ownList.value?.isEveryRowOpen,
  set: (isExpanded: boolean) => ownList.value?.setAllRowsOpen(isExpanded),
});
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";
.irp-opportunities-tab {
  @apply flex flex-col gap-16;
}

.irp-opportunities-tab__header {
  @apply flex items-center justify-between gap-8;
}

.irp-opportunities-tab__heading {
  @apply text-base font-bold text-neutral-text-strong;
}

.irp-opportunities-tab__card {
  @apply rounded-16 border border-neutral-border bg-white py-24 pl-24 pr-16 shadow-component;
}

.irp-opportunities-tab__skeleton {
  @apply flex flex-col gap-16;
}
</style>
